import Auth from '@aws-amplify/auth';
import { store } from 'store';
import { CurrentUserSessionHelper } from 'utility/CurrentUserSessionHelper';

/**
 * Base class for every error blustreamApi can reject with. Carries a `kind`
 * discriminant so consumers can catch-all (`instanceof BlustreamApiError`),
 * switch on the tagged union (`switch (err.kind)`), or match one specific
 * type (`instanceof TimeoutError`). Not thrown directly. (story #59925)
 * @property {('http'|'auth'|'timeout'|'network')} kind
 */
export class BlustreamApiError extends Error {
  constructor(kind, message) {
    super(message);
    this.name = 'BlustreamApiError';
    this.kind = kind;
  }
}

/**
 * Typed error thrown by apiFetch on non-2xx responses.
 * @property {number} status - HTTP status code
 * @property {object|null} body - parsed response body, or null when unparseable
 * @property {Array} details - field-level validation details from body.details
 */
export class ApiError extends BlustreamApiError {
  constructor(status, body, message) {
    super('http', message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.details = (body && body.details) || [];
  }
}

/**
 * Thrown by getAuthHeaders before any request is dispatched when the caller
 * cannot be authenticated/scoped — so blustreamApi never sends an unauthenticated
 * or unscoped request (story #59924, AC5).
 * @property {('no-session'|'no-client-id')} reason - why auth could not be resolved
 */
export class AuthError extends BlustreamApiError {
  constructor(reason, message) {
    super('auth', message || reason);
    this.name = 'AuthError';
    this.reason = reason;
  }
}

/**
 * Thrown by apiFetch when a request exceeds its timeout (story #59925).
 * @property {number} timeoutMs - the timeout that elapsed
 */
export class TimeoutError extends BlustreamApiError {
  constructor(timeoutMs) {
    super('timeout', `Request timed out after ${timeoutMs}ms`);
    this.name = 'TimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/**
 * Thrown by apiFetch when the underlying fetch rejects (network drop, DNS,
 * CORS) — i.e. the request never produced an HTTP response (story #59925).
 * @property {Error} cause - the original fetch rejection
 */
export class NetworkError extends BlustreamApiError {
  constructor(cause) {
    super('network', 'Network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Resolves the base URL for the BETA API gateway.
 * Driven by the REACT_APP_BLUSTREAM_API_BASE_URL env var (set per environment).
 * Returns empty string when unset so callers can detect not-yet-configured.
 * @returns {string}
 */
export function getBaseUrl() {
  return process.env.REACT_APP_BLUSTREAM_API_BASE_URL || '';
}

/**
 * Builds the headers attached to every BETA API request.
 *
 * Source of truth (story #59924, AC4): the Bearer token comes from Amplify
 * `Auth.currentSession()`, which transparently refreshes an expired access
 * token via the stored Cognito refresh token — callers never pass a token.
 * The client context comes from `loginReducer.groups[0]` (same source as the
 * legacy RESTApi client).
 *
 * Controlled failure (AC5): throws AuthError BEFORE any request when there is
 * no usable session or no resolved client id, so apiFetch can never dispatch
 * an unauthenticated or unscoped request. The caller / a global handler
 * decides whether to redirect to login.
 *
 * @returns {Promise<Record<string,string>>}
 * @throws {AuthError} reason 'no-session' or 'no-client-id'
 */
export async function getAuthHeaders() {
  let token;
  try {
    const session = await Auth.currentSession();
    token = session.getAccessToken().getJwtToken();
  } catch (e) {
    throw new AuthError('no-session', 'No authenticated Cognito session');
  }
  if (!token) throw new AuthError('no-session', 'No access token in session');

  // The active client's external_uuid comes from the first customer cognito
  // group (the gateway/gandalf expect external_uuid, not a numeric id).
  // getCurrentCustomerId filters out role groups (admin, roadmin, ROLE_*) so
  // they can't be sent as clientId — admin cross-tenant auth is #59957.
  const clientId = CurrentUserSessionHelper.getCurrentCustomerId(store.getState()?.loginReducer?.groups);
  if (!clientId) throw new AuthError('no-client-id', 'No resolved BluStream-Client-ID');

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'BluStream-Client-ID': clientId,
  };
}

/** Default per-request timeout (ms). Override per call via opts.timeoutMs. (story #59925) */
export const DEFAULT_TIMEOUT_MS = 30000;

/**
 * Retry policy for blustreamApi requests. Epic 1 ships ZERO automatic retries
 * ("log errors only") — `attempts: 1` means one try, no retry. The single
 * request attempt in apiFetch is the seam where #59967 will add the retry
 * loop + backoff; retries must not fight Amplify's transparent token refresh
 * in getAuthHeaders (#59924), which is part of that tuning. (story #59925)
 * @property {number} attempts - total attempts (1 = no retry)
 * @property {string[]} retryableMethods - methods eligible for retry (none yet)
 * @property {number[]} retryableStatuses - statuses eligible for retry (none yet)
 */
export const RETRY_POLICY = {
  attempts: 1,
  retryableMethods: [],
  retryableStatuses: [],
};

/**
 * Central failure logging for blustreamApi. Logs each surfaced failure once.
 * Initial behavior is console-only (no telemetry sink exists yet); wiring
 * telemetry is deferred to story #59967. (story #59925)
 * @param {ApiError|TimeoutError|NetworkError} err - AuthError is intentionally not logged (thrown before the request)
 * @param {{method?: string, path: string}} context
 */
export function logFailure(err, context) {
  // eslint-disable-next-line no-console
  console.error('[blustreamApi] request failed', {
    method: context.method || 'GET',
    path: context.path,
    kind: err.kind,
    status: err.status,
  });
}

/**
 * Maps any error into a list of human-readable strings, shaped to feed
 * src/utility/showValidationErrors.js or inline rendering. Page stories own
 * whether to render via toast vs inline; refined UX is story #59967.
 * (story #59925)
 * @param {unknown} err
 * @returns {string[]}
 */
export function toUserMessages(err) {
  if (err instanceof ApiError) {
    const fieldMessages = (err.details || []).map((d) => d.message).filter(Boolean);
    if (fieldMessages.length > 0) return fieldMessages;
    return [err.body?.message || 'Something went wrong. Please try again.'];
  }
  if (err instanceof TimeoutError) return ['The request timed out. Please try again.'];
  if (err instanceof NetworkError) return ['Network error. Check your connection and try again.'];
  if (err instanceof AuthError) return ['Your session has expired. Please sign in again.'];
  return ['Something went wrong. Please try again.'];
}

/**
 * Single fetch wrapper for all BETA API calls.
 *
 * Attaches Cognito Bearer + BluStream-Client-ID headers, prepends the
 * configured base URL, parses JSON, returns null on 204.
 *
 * Network behavior (story #59925):
 * - Timeout: aborts after `opts.timeoutMs` (default DEFAULT_TIMEOUT_MS) and
 *   throws TimeoutError. The internal AbortController also forwards a
 *   caller-supplied `opts.signal`; a deliberate caller abort is rethrown
 *   as-is (not surfaced/logged as a failure).
 * - Errors: every failure rejects as a typed BlustreamApiError — ApiError (non-2xx,
 *   incl. 401), TimeoutError (timeout), NetworkError (fetch rejection). Each
 *   surfaced failure is logged once via logFailure.
 * - Retries: none in Epic 1 (see RETRY_POLICY); tuning is #59967.
 * - Token refresh: Amplify refreshes transparently in getAuthHeaders; a 401
 *   means the session is genuinely unusable. Redirect/signOut is a
 *   caller/global concern (#59924); retry/UX refinement is #59967.
 *
 * @param {string} path - resource-relative path including leading slash, e.g. '/customers/1'; the version segment (e.g. /beta) is carried by the base URL
 * @param {object} [opts] - fetch options plus blustreamApi extras. `opts.timeoutMs`
 *   overrides the default timeout (stripped before fetch). `opts.signal`
 *   (AbortController.signal) cancels the request. Caller `headers` merge over defaults.
 * @returns {Promise<any>} parsed JSON body, or null on 204
 * @throws {BlustreamApiError} ApiError | TimeoutError | NetworkError (or AuthError from getAuthHeaders)
 */
export async function apiFetch(path, opts = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal: callerSignal, headers: callerHeaders, ...rest } = opts;

  const defaultHeaders = await getAuthHeaders();
  const headers = { ...defaultHeaders, ...(callerHeaders || {}) };

  const controller = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) {
      // Signal was already aborted before fetch started — bail out immediately,
      // just like native fetch does. Not a failure; don't log. (story #59925)
      throw new DOMException('The user aborted a request.', 'AbortError');
    }
    callerSignal.addEventListener('abort', onCallerAbort);
  }
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  let res;
  try {
    // single attempt — RETRY_POLICY.attempts === 1; retry loop added by #59967
    res = await fetch(`${getBaseUrl()}${path}`, { ...rest, headers, signal: controller.signal });
  } catch (e) {
    if (timedOut) {
      const err = new TimeoutError(timeoutMs);
      logFailure(err, { method: rest.method, path });
      throw err;
    }
    if (callerSignal && callerSignal.aborted) throw e; // deliberate caller cancel — not a failure
    const err = new NetworkError(e);
    logFailure(err, { method: rest.method, path });
    throw err;
  } finally {
    clearTimeout(timer);
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
  }

  if (res.status === 401) {
    const err = new ApiError(401, null, 'Unauthorized');
    logFailure(err, { method: rest.method, path });
    throw err;
  }
  if (res.status === 204) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = body && body.message
      ? `HTTP ${res.status}: ${body.message}`
      : `HTTP ${res.status}`;
    const err = new ApiError(res.status, body, msg);
    logFailure(err, { method: rest.method, path });
    throw err;
  }
  return res.json();
}
